/**
 * Shared preset copy for {@link AlertDialog} — mirrors production flows (approve / reject / delete / …)
 * while keeping one compound component.
 */

export type AlertDialogVariant =
  | 'default'
  | 'destructive'
  | 'approve'
  | 'reject'
  | 'delete'
  | 'deactivate'
  | 'cancelAction'
  | 'viewRemarks'
  | 'single';

const ENTITY_FALLBACK = 'this item';

export function resolveSubject(subject: string | undefined): string {
  const s = subject?.trim();
  return s && s.length > 0 ? s : ENTITY_FALLBACK;
}

export function presetAlertTitle(variant: AlertDialogVariant, subject?: string): string {
  const entity = resolveSubject(subject);

  switch (variant) {
    case 'viewRemarks':
      return 'Rejection reason';
    case 'destructive':
    case 'delete':
      return `Are you sure you want to delete ${entity}?`;
    case 'deactivate':
      return `Are you sure you want to deactivate ${entity}?`;
    case 'approve':
      return `Are you sure you want to approve ${entity}?`;
    case 'reject':
      return `Are you sure you want to reject ${entity}?`;
    case 'cancelAction':
      return `Are you sure you want to cancel ${entity}?`;
    case 'single':
      return entity;
    case 'default':
      return `Are you sure you want to confirm ${entity}?`;
    default:
      return `Are you sure you want to confirm ${entity}?`;
  }
}

export function presetAlertDescription(variant: AlertDialogVariant): string | undefined {
  switch (variant) {
    case 'delete':
    case 'destructive':
      return 'This action cannot be undone.';
    case 'deactivate':
      return 'You can reactivate later if your process allows it.';
    case 'viewRemarks':
    case 'single':
      return undefined;
    default:
      return undefined;
  }
}

export function presetCancelLabel(variant: AlertDialogVariant): string {
  switch (variant) {
    case 'approve':
    case 'reject':
      return 'No';
    case 'viewRemarks':
    case 'single':
      return 'Close';
    case 'cancelAction':
      return 'Go back';
    default:
      return 'Cancel';
  }
}

export function presetActionLabel(variant: AlertDialogVariant): string {
  switch (variant) {
    case 'approve':
      return 'Approve';
    case 'reject':
      return 'Submit';
    case 'delete':
      /** Matches legacy {@link AlertPopup} — primary action label, not the verb in the title. */
      return 'Confirm';
    case 'destructive':
      return 'Delete';
    case 'deactivate':
      return 'Deactivate';
    case 'cancelAction':
      return 'Confirm';
    case 'viewRemarks':
    case 'single':
      return 'OK';
    default:
      return 'Confirm';
  }
}

/**
 * Maps preset flows to action button tone. Matches production {@link AlertPopup}: confirm is almost
 * always `primary` (purple). Use Root `variant="destructive"` only when you need an explicit red primary.
 */
export function actionButtonVariantForPreset(
  variant: AlertDialogVariant,
): 'default' | 'destructive' {
  if (variant === 'destructive') {
    return 'destructive';
  }
  return 'default';
}
